import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  model,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';
import { RouteReuseStrategy } from '@angular/router';
import {
  UserSettingComponent,
  UserSettingViewModel,
} from '@components/user-setting/user-setting.component';
import { AccountService } from '@services/api/account.service';
import { AuthService } from '@services/auth.service';
import { CustomRouteReuseStrategy } from '@services/custom-route-reuse-strategy';
import { firstValueFrom } from 'rxjs';
import { NotificationViewModel } from '@components/notification-list/notification-list-table/notification-list-table.component';
import { MatDividerModule } from '@angular/material/divider';
import { NotificationDetailDialogComponent } from './notification-detail-dialog/notification-detail-dialog.component';
import { NotificationService } from '@services/api/notification.service';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { CommonModule } from '@angular/common';
import { LoadingService } from '@services/loading.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LogService } from '@services/api/log.service';
import { appVersion } from 'src/app/app.component';
import { DocumentService } from '@services/api/document.service';
import { HttpResponse } from '@angular/common/http';
@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatMenuModule,
    MatListModule,
    MatDividerModule,
    MatProgressSpinner,
    CommonModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnChanges, AfterViewInit {
  /** 掲示板メッセージ一覧 */
  @Input() public notificationList: NotificationViewModel[] = [];

  /** 掲示板メッセージ一覧表示ポップアップのメニューを表示するか */
  @Input() public notificationEnable: boolean = true;

  /** 掲示板メッセージ最終表示ポップアップ日時*/
  @Input() public lastReadAt: string = '';

  /** サイドメニューの切り替え有効化 */
  @Input() sidenav = false;

  /** 新規登録を通知 */
  @Output() notificationListUpdated = new EventEmitter<Date>();

  /** ログアウトを通知 */
  @Output() loggedOut = new EventEmitter<void>();

  /** サイドメニューの開閉状態 */
  public opened = model<boolean>(true);

  /** 掲示板メッセージの未読数 */
  public unreadCount: number = 0;

  /** ローディング */
  public loading: boolean = false;

  /** バージョン */
  public readonly version: string = appVersion;

  /** 掲示板メッセージ取得開始日時 */
  private historyTo = new Date();

  /** マニュアルのファイル名の正規表現 */
  private readonly manualFilenameRegex = /filename\*?=(?:UTF-8'')?(.+)/;

  constructor(
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private accountService: AccountService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private routeReuseStrategy: RouteReuseStrategy,
    private loadingService: LoadingService,
    private logService: LogService,
    private documentService: DocumentService,
  ) {}

  public ngOnInit(): void {
    this.initNotificationList();
  }

  public ngAfterViewInit(): void {
    this.initNotificationList();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['lastReadAt'] !== undefined) {
      this.lastReadAt = changes['lastReadAt'].currentValue;
    }

    if (changes['notificationList'] !== undefined) {
      this.notificationList = changes['notificationList'].currentValue;

      // 掲示板メッセージ一覧の更新
      this.initNotificationList();
    }
  }

  /**
   * サイドメニューの開閉状態を切り替える
   */
  public toggleSidenav() {
    this.opened.update((opened) => !opened);
  }

  /** ログアウト */
  public logout() {
    this.loggedOut.emit();

    // 再利用ルート情報のキャッシュをクリアする
    (this.routeReuseStrategy as CustomRouteReuseStrategy).refresh();
  }

  /**
   * ユーザー設定画面を開く
   */
  public async openUserSetting() {
    const account = await firstValueFrom(
      this.accountService.findLoggedInUser(),
    );

    // ダイアログに送信するデータをログインユーザー情報から定義
    const options: UserSettingViewModel = {
      email: account.email,
      mfaRequired: account.mfaRequired,
      mfaStatus: account.mfaStatus,
    };

    // ダイアログで入力したフォームから値を取得
    const userSetting: UserSettingViewModel = await firstValueFrom(
      this.dialog
        .open(UserSettingComponent, {
          width: '700px',
          data: options,
        })
        .afterClosed(),
    );

    // パスワードの更新
    if (!userSetting?.oldPassword || !userSetting?.passwordConfirm) {
      return;
    }

    try {
      await firstValueFrom(
        this.authService.changePassword(
          userSetting.oldPassword,
          userSetting.passwordConfirm,
        ),
      );
      // ログの登録自体は失敗しても処理を継続する。
      this.logService.add('password_change').subscribe({
        error: () => {
          // ログ埋め込み処理が失敗した場合でも処理を止めない。
        },
      });
      this.displaySnackBar();
    } catch (_) {
      // エラーダイアログを表示
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: `入力された現在のパスワードが間違っています。正しいパスワードを入力してください。`,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
    }
  }

  /**
   * ユーザー設定更新お知らせのスナックバー表示
   */
  private displaySnackBar(): void {
    // 更新完了表示
    this.snackBar.open('ユーザー設定を更新しました', '', {
      duration: 2000,
      panelClass: ['matp-snackbar'],
    });
  }

  /**
   * 掲示板メッセージ詳細表示ポップアップを表示
   */
  public openNotificationDetailDialog(
    notification: NotificationViewModel,
    event: MouseEvent,
  ) {
    // メニューを閉じないようにする
    event.stopPropagation();

    const options: NotificationViewModel = { ...notification };
    this.dialog.open(NotificationDetailDialogComponent, {
      width: '700px',
      data: options,
    });
  }

  /**
   * 掲示板メッセージ一覧表示ポップアップを開いた日時を登録
   */
  public markAsRead(): void {
    this.notificationService.markAsRead().subscribe();
    this.loadingService.clear();
  }

  /**
   * 掲示板メッセージ一覧ポップアップ更新(現状の掲示板メッセージ一覧にさらに5件追加)
   */
  public updateNotificationList(event: MouseEvent): void {
    this.notificationListUpdated.emit(this.historyTo);
    // ヘッダーのメニューでのみローディング
    this.loading = true;
    // 全体ローディングをしない
    this.loadingService.clear();
    // メニューを閉じないようにする
    event.stopPropagation();
  }

  /**
   * 掲示板メッセージ一覧ポップアップを閉じた時の処理
   */
  public closeNotificationMenu(): void {
    // 未読数の計算
    this.refreshUnreadCount();
    // 既読にする
    this.markNotificationListAsRead();
  }

  /**
   * マニュアルのダウンロードを行う。
   */
  public getTracksManual(): void {
    this.documentService.downloadManual().subscribe({
      next: (response: HttpResponse<Blob>) => {
        if (!response.body) {
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message:
              'マニュアルダウンロードに失敗しました。\nしばらく待ってから再度お試しください。\nそれでも失敗する場合は、管理者にお問い合わせください。',
          };
          this.dialog.open(AlertDialogComponent, {
            width: '650px',
            data: alertDialogOptions,
          });

          console.error('Failed to download manual: Blob is null');
          return;
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Macnica Tracks 操作マニュアル.pdf'; // デフォルトのファイル名

        if (contentDisposition) {
          const matches = this.manualFilenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = decodeURIComponent(matches[1]);
          }
        }

        const url = window.URL.createObjectURL(response.body);
        const downloadObj = document.createElement('a');
        downloadObj.href = url;
        downloadObj.download = filename;
        document.body.appendChild(downloadObj);
        downloadObj.click();
        document.body.removeChild(downloadObj);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message:
            'マニュアルダウンロードに失敗しました。\nしばらく待ってから再度お試しください。\nそれでも失敗する場合は、管理者にお問い合わせください。',
        };
        this.dialog.open(AlertDialogComponent, {
          width: '650px',
          data: alertDialogOptions,
        });

        console.error('Failed to download manual.', error);
        return;
      },
    });
  }

  /**
   * 掲示板メッセージ一覧ポップアップ表示
   */
  private initNotificationList(): void {
    // 掲示板メッセージを投稿日時(releaseAt)で降順に並び替える
    this.notificationList.sort((a, b) => {
      return new Date(b.releaseAt).getTime() - new Date(a.releaseAt).getTime();
    });

    // 未読数の計算
    this.refreshUnreadCount();

    // 検索開始日時を更新
    const lastNotification = this.notificationList.at(-1);
    if (lastNotification && lastNotification.releaseAt) {
      this.historyTo = new Date(lastNotification.releaseAt);
    }

    // 自動スクロール
    setTimeout(() => {
      this.scrollToNotificationListBottom();
      this.loading = false;
    });
  }

  /**
   * 掲示板メッセージ一覧ポップアップの再下端まで自動スクロールする
   */
  private scrollToNotificationListBottom(): void {
    const lastItemIndex = this.notificationList.length * 2 + 1;
    const lastItem = document.querySelector(
      `#mat-menu-panel-1 > div > mat-list > mat-list-item:nth-child(${lastItemIndex})`,
    );
    if (lastItem !== null) {
      lastItem.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  /**
   * 未読数の計算
   */
  private refreshUnreadCount(): void {
    // 既読未読判定
    this.notificationList.map((notification) => {
      if (
        this.lastReadAt &&
        new Date(notification.releaseAt) <= new Date(this.lastReadAt)
      ) {
        notification.isRead = true;
      } else {
        notification.isRead = false;
      }
    });

    // 未読数の計算
    this.unreadCount = this.notificationList.filter(
      (notification) => notification.isRead === false,
    ).length;
  }

  /**
   * 取得した掲示板メッセージをすべて既読にする
   */
  private markNotificationListAsRead(): void {
    this.notificationList.map((notification) => (notification.isRead = true));

    // 未読数の計算
    this.unreadCount = this.notificationList.filter(
      (notification) => notification.isRead === false,
    ).length;
  }
}
